import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Table, Form, Button, Row, Col } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';

import Loader from '../../../components/Common/Loader/Loader';
import getServiceRegionsQuery from './getServiceRegions.graphql';
import upsertServiceRegionMutation from './upsertServiceRegion.graphql';
import getWaitlistedWorkersQuery from './getWaitlistedWorkers.graphql';
import promoteWorkerMutation from './promoteWorker.graphql';
import getWaitlistLeadsQuery from './getWaitlistLeads.graphql';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'closed', label: 'Closed' },
];

const badgeStyle = (status) => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
  color: '#fff',
  background: status === 'open' ? '#2E7D32' : status === 'waitlist' ? '#ED9C28' : '#9AA0A6',
});

const ServiceRegions = () => {
  const { loading, data, refetch } = useQuery(getServiceRegionsQuery, { fetchPolicy: 'network-only' });
  const [save, { loading: saving }] = useMutation(upsertServiceRegionMutation);
  const { data: wlData, refetch: refetchWaitlist } = useQuery(getWaitlistedWorkersQuery, { fetchPolicy: 'network-only' });
  const [promote, { loading: promoting }] = useMutation(promoteWorkerMutation);

  const waitlisted = wlData?.getWaitlistedWorkers?.result || [];
  const { data: leadData } = useQuery(getWaitlistLeadsQuery, { fetchPolicy: 'network-only' });
  const leads = leadData?.getWaitlistLeads?.result || [];

  const onPromote = async (w) => {
    try {
      const res = await promote({ variables: { userId: w.id } });
      const out = res?.data?.promoteWorker;
      if (out?.status === 200) {
        toastr.success('Promoted', `${w.firstName || ''} ${w.lastName || ''} is now active.`);
        await Promise.all([refetch(), refetchWaitlist()]);
      } else {
        toastr.error('Error', out?.errorMessage || 'Could not promote.');
      }
    } catch (e) {
      toastr.error('Error', e.message || 'Could not promote.');
    }
  };

  // Per-row local edits keyed by stateCode: { status, maxWorkers }
  const [edits, setEdits] = useState({});
  const [newState, setNewState] = useState('');

  const regions = data?.getServiceRegions?.result || [];
  const allStates = data?.getServiceRegions?.states || [];

  // States not yet managed → available to add (anything without a row is Closed by default).
  const addableStates = useMemo(() => {
    const managed = new Set(regions.map(r => r.stateCode));
    return allStates.filter(s => !managed.has(s.code));
  }, [regions, allStates]);

  const valueFor = (r, field) => {
    const e = edits[r.stateCode] || {};
    if (field in e) return e[field];
    return field === 'maxWorkers' ? (r.maxWorkers == null ? '' : r.maxWorkers) : r[field];
  };

  const setEdit = (stateCode, field, value) =>
    setEdits(prev => ({ ...prev, [stateCode]: { ...prev[stateCode], [field]: value } }));

  const persist = async (stateCode, status, maxWorkers) => {
    try {
      const res = await save({
        variables: {
          stateCode,
          status,
          maxWorkers: maxWorkers === '' || maxWorkers == null ? null : parseInt(maxWorkers, 10),
        },
      });
      const out = res?.data?.upsertServiceRegion;
      if (out?.status === 200) {
        toastr.success('Saved', `${stateCode} updated.`);
        setEdits(prev => { const n = { ...prev }; delete n[stateCode]; return n; });
        await refetch();
      } else {
        toastr.error('Error', out?.errorMessage || 'Could not save.');
      }
    } catch (e) {
      toastr.error('Error', e.message || 'Could not save.');
    }
  };

  const onSaveRow = (r) => persist(r.stateCode, valueFor(r, 'status'), valueFor(r, 'maxWorkers'));

  const onAddState = async () => {
    if (!newState) return;
    await persist(newState, 'closed', null);
    setNewState('');
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 className="siteAdminPrimaryHeading">Service Regions</h1>
      <p style={{ color: '#5f6368', maxWidth: 760 }}>
        Control where BestHand operates, state by state. A state that is <b>not listed below is Closed
        by default</b> — clients there can’t book and new pros are waitlisted. Set a state to
        <b> Open</b> to start taking jobs, <b>Waitlist</b> to collect demand without operating yet, or
        <b> Closed</b> to stop. Set a <b>Max workers</b> cap to waitlist new pros once a state is full
        (leave blank for unlimited).
      </p>

      <Loader type="page" show={loading}>
        {!loading && (
          <>
            <Table responsive bordered hover style={{ marginTop: 16, background: '#fff' }}>
              <thead>
                <tr>
                  <th>State</th>
                  <th style={{ width: 160 }}>Status</th>
                  <th style={{ width: 140 }}>Max workers</th>
                  <th style={{ width: 110 }}>Active pros</th>
                  <th style={{ width: 120 }}>Unactivated pros</th>
                  <th style={{ width: 110 }}>Waitlisted</th>
                  <th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {regions.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9AA0A6' }}>
                    No regions configured yet — every state is Closed. Add one below to open it.
                  </td></tr>
                )}
                {regions.map(r => (
                  <tr key={r.stateCode}>
                    <td>
                      <b>{r.stateName}</b> <span style={{ color: '#9AA0A6' }}>({r.stateCode})</span>
                      <div style={{ marginTop: 4 }}><span style={badgeStyle(r.status)}>{r.status}</span></div>
                    </td>
                    <td>
                      <Form.Control as="select" value={valueFor(r, 'status')}
                        onChange={e => setEdit(r.stateCode, 'status', e.target.value)}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </Form.Control>
                    </td>
                    <td>
                      <Form.Control type="number" min="0" placeholder="Unlimited"
                        value={valueFor(r, 'maxWorkers')}
                        onChange={e => setEdit(r.stateCode, 'maxWorkers', e.target.value)} />
                    </td>
                    <td style={{ textAlign: 'center' }}>{r.activeWorkers}</td>
                    <td style={{ textAlign: 'center', color: '#5f6368' }}>{r.unactivatedWorkers}</td>
                    <td style={{ textAlign: 'center' }}>
                      {r.waitlistedWorkers > 0
                        ? <b style={{ color: '#ED9C28' }}>{r.waitlistedWorkers}</b>
                        : r.waitlistedWorkers}
                    </td>
                    <td>
                      <Button size="sm" disabled={saving} onClick={() => onSaveRow(r)}>Save</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <Row style={{ marginTop: 20, alignItems: 'center' }}>
              <Col xs="auto"><b>Add a state:</b></Col>
              <Col xs="auto" style={{ minWidth: 240 }}>
                <Form.Control as="select" value={newState} onChange={e => setNewState(e.target.value)}>
                  <option value="">Select a state…</option>
                  {addableStates.map(s => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
                </Form.Control>
              </Col>
              <Col xs="auto">
                <Button variant="success" disabled={!newState || saving} onClick={onAddState}>Add</Button>
              </Col>
            </Row>

            <h2 className="siteAdminSecondaryHeading" style={{ marginTop: 40 }}>
              Waitlisted Pros {waitlisted.length > 0 && <span style={{ color: '#ED9C28' }}>({waitlisted.length})</span>}
            </h2>
            <p style={{ color: '#5f6368', maxWidth: 760 }}>
              Pros who signed up while their state was Closed/Waitlist or full. They’re excluded from
              job matching until you promote them — do that when you open their state or a slot frees.
            </p>
            <Table responsive bordered hover style={{ background: '#fff' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>State</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {waitlisted.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9AA0A6' }}>No pros on the waitlist.</td></tr>
                )}
                {waitlisted.map(w => (
                  <tr key={w.id}>
                    <td><b>{`${w.firstName || ''} ${w.lastName || ''}`.trim() || '—'}</b></td>
                    <td>{w.stateCode || w.state || '—'}</td>
                    <td>{w.email || '—'}</td>
                    <td>{w.phoneNumber || '—'}</td>
                    <td>
                      <Button size="sm" variant="success" disabled={promoting} onClick={() => onPromote(w)}>Promote</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h2 className="siteAdminSecondaryHeading" style={{ marginTop: 40 }}>
              Waitlist Leads {leads.length > 0 && <span style={{ color: '#2E7D32' }}>({leads.length})</span>}
            </h2>
            <p style={{ color: '#5f6368', maxWidth: 760 }}>
              Clients who tried to book in a state that isn’t Open — captured demand. Use it to decide
              where to expand next.
            </p>
            <Table responsive bordered hover style={{ background: '#fff' }}>
              <thead>
                <tr>
                  <th>State</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th style={{ width: 90 }}>Source</th>
                  <th style={{ width: 170 }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9AA0A6' }}>No waitlist demand captured yet.</td></tr>
                )}
                {leads.map(l => (
                  <tr key={l.id}>
                    <td><b>{l.stateCode || l.state || '—'}</b></td>
                    <td>{l.email || '—'}</td>
                    <td>{l.phoneNumber || '—'}</td>
                    <td>{l.source || '—'}</td>
                    <td>{l.createdAt ? new Date(l.createdAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Loader>
    </div>
  );
};

export default ServiceRegions;
